// ---------------------------------------------------------------------------
// Currency system. All monetary amounts are stored in USD (base). The active
// display currency + live FX rates are held in module-level state that
// CurrencyContext keeps in sync SYNCHRONOUSLY (before React re-renders), so
// formatters below always read fresh values.
// ---------------------------------------------------------------------------
export const CURRENCY_META = {
  USD: { code: 'USD', label: 'US Dollar', locale: 'en-US', symbol: '$', frac: 0 },
  INR: { code: 'INR', label: 'Indian Rupee', locale: 'en-IN', symbol: '₹', frac: 0 },
  AED: { code: 'AED', label: 'UAE Dirham', locale: 'en-AE', symbol: 'AED ', frac: 0 },
  SAR: { code: 'SAR', label: 'Saudi Riyal', locale: 'en-SA', symbol: 'SAR ', frac: 0 },
  EUR: { code: 'EUR', label: 'Euro', locale: 'en-IE', symbol: '€', frac: 0 },
  GBP: { code: 'GBP', label: 'British Pound', locale: 'en-GB', symbol: '£', frac: 0 },
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_META);

const _active = {
  currency: 'USD',
  rates: { USD: 1, INR: 83, AED: 3.6725, SAR: 3.75, EUR: 0.92, GBP: 0.79 },
};

export const setActiveCurrency = (currency, rates) => {
  if (currency && CURRENCY_META[currency]) _active.currency = currency;
  if (rates && typeof rates === 'object') _active.rates = { ..._active.rates, ...rates };
};

export const getActiveCurrency = () => _active.currency;

const _meta = () => CURRENCY_META[_active.currency] || CURRENCY_META.USD;
const _rate = () => Number(_active.rates[_active.currency] || 1);

// Convert a USD-base amount to the active currency.
export const toActive = (usd) => Number(usd || 0) * _rate();

export const fmtMoney = (v, frac) => {
  const meta = _meta();
  const val = toActive(v);
  const f = frac === undefined || frac === null ? meta.frac : frac;
  return new Intl.NumberFormat(meta.locale, {
    style: 'currency', currency: meta.code,
    minimumFractionDigits: f, maximumFractionDigits: f,
  }).format(val);
};

export const fmtMoneyShort = (v) => {
  const meta = _meta();
  const n = toActive(v);
  const sym = meta.symbol;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}k`;
  return `${sym}${new Intl.NumberFormat(meta.locale).format(Math.round(n))}`;
};

export const fmtNum = (v) => new Intl.NumberFormat('en-US').format(Number(v || 0));

export const fmtDate = (s) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '-'; }
};

// Full date + time, e.g. "14 Aug 2026, 8:14 PM" (used for Detected date/time).
export const fmtDateTime = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return '-'; }
};

export const fmtRelative = (s) => {
  if (!s) return '';
  const d = new Date(s);
  const diff = (Date.now() - d.getTime()) / 1000;
  const day = 86400;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < day) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < day * 30) return `${Math.floor(diff / day)}d ago`;
  return fmtDate(s);
};

// Lifecycle status -> badge classes (premium, ring-bordered)
export const STATUS_META = {
  'Detected': 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  'Drafted': 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300',
  'Internal Review': 'bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100',
  'Submitted': 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  'Negotiation': 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  'Approved': 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100',
  'Invoiced': 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  'Paid': 'bg-money-600 text-white ring-1 ring-inset ring-money-700',
  // detection statuses
  'new': 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  'converted': 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100',
  'dismissed': 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200',
};

export const CO_STATUSES = [
  'Detected', 'Drafted', 'Internal Review', 'Submitted',
  'Negotiation', 'Approved', 'Invoiced', 'Paid',
];

export const ROLE_LABEL = {
  project_manager: 'Project Manager',
  owner: 'Owner / Executive',
  foreman: 'Field Foreman',
  accountant: 'Accountant / Billing',
};

export const SOURCE_LABEL = {
  email: 'Email', drawing: 'Drawing', rfi: 'RFI', photo: 'Site Photo',
  meeting_notes: 'Meeting Notes', whatsapp: 'WhatsApp', daily_report: 'Daily Report',
  timesheet: 'Timesheet', site_instruction: 'Site Instruction', contract: 'Contract',
  meeting_minutes: 'Meeting Minutes', other: 'Other',
};

// ---- Validation platform labels ----
export const RISK_META = {
  low: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100',
  medium: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  high: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-100',
};

export const ANALYSIS_STATUS_META = {
  scope_change: { label: 'Scope change', cls: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100' },
  no_change: { label: 'In scope', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100' },
  failed: { label: 'Analysis failed', cls: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-100' },
  skipped: { label: 'Not analyzed', cls: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200' },
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200' },
};

export const VERDICT_LABEL = {
  correct: 'Correct', incorrect: 'Incorrect', needs_improvement: 'Needs Improvement',
};

export const REASON_OPTIONS = [
  { value: 'inside_original_scope', label: 'Inside original scope' },
  { value: 'missed_supporting_evidence', label: 'Missed supporting evidence' },
  { value: 'wrong_contractual_interpretation', label: 'Wrong contractual interpretation' },
  { value: 'wrong_cost_estimate', label: 'Wrong cost estimate' },
  { value: 'wrong_affected_trade', label: 'Wrong affected trade' },
  { value: 'requires_more_project_context', label: 'Requires more project context' },
  { value: 'other', label: 'Other' },
];

export const UPLOAD_SOURCE_OPTIONS = [
  'email', 'rfi', 'site_instruction', 'meeting_minutes', 'drawing',
  'daily_report', 'whatsapp', 'contract', 'other',
];

export const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

// Plain-language notice deadline countdown for PMs.
export const noticeCountdown = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return null;
  const d = Number(daysLeft);
  if (d < 0) return { label: `Overdue by ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'}`, tone: 'danger', urgent: true };
  if (d === 0) return { label: 'Due today', tone: 'danger', urgent: true };
  if (d <= 3) return { label: `Due in ${d} day${d === 1 ? '' : 's'}`, tone: 'warn', urgent: true };
  if (d <= 7) return { label: `${d} days left`, tone: 'warn', urgent: false };
  return { label: `${d} days left`, tone: 'ok', urgent: false };
};

export const NOTICE_TONE = {
  danger: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-100',
  warn: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  ok: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
};

// ---- V1 Opportunity terminology (PM-facing) ----
export const OPPORTUNITY_TYPE_LABEL = {
  variation: 'Variation',
  claim: 'Claim',
  eot: 'Extension of Time',
  cost_impact: 'Cost Impact',
  none: 'No Opportunity',
};

// Type badge styling (calm, non-alarming; money/brand/warn tokens only)
export const OPPORTUNITY_TYPE_META = {
  variation: 'bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100',
  claim: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  eot: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  cost_impact: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100',
  none: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200',
};

// Plain-language strength (replaces confidence %)
export const STRENGTH_META = {
  strong: { label: 'Strong case', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100', dot: 'bg-money-600' },
  worth_checking: { label: 'Worth checking', cls: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100', dot: 'bg-warn-600' },
  weak: { label: 'Weak', cls: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200', dot: 'bg-slate-400' },
};

export const strengthFromConfidence = (c) => {
  const v = Number(c || 0);
  if (v >= 0.75) return 'strong';
  if (v >= 0.45) return 'worth_checking';
  return 'weak';
};

// Non-linear opportunity workflow statuses (ordered for the tracker board)
export const OPPORTUNITY_STATUSES = [
  'new', 'reviewed', 'notice_sent', 'submitted', 'approved', 'rejected', 'settled', 'dismissed',
];

export const OPPORTUNITY_STATUS_META = {
  new: { label: 'New', cls: 'bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100' },
  reviewed: { label: 'Reviewed', cls: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300' },
  notice_sent: { label: 'Notice Sent', cls: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100' },
  submitted: { label: 'Submitted', cls: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200' },
  approved: { label: 'Approved', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-100' },
  rejected: { label: 'Rejected', cls: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-100' },
  settled: { label: 'Settled', cls: 'bg-money-600 text-white ring-1 ring-inset ring-money-700' },
  dismissed: { label: 'Dismissed', cls: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200' },
};

// ---- Phase 12 pilot: classification, decision, pricing status ----
export const CLASSIFICATION_META = {
  potential_change: { label: 'Potential change', cls: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200', dot: 'bg-amber-500' },
  needs_review: { label: 'Needs review', cls: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200', dot: 'bg-sky-500' },
  confirmed_change: { label: 'Confirmed change', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-200', dot: 'bg-money-600' },
  likely_included: { label: 'Likely included', cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', dot: 'bg-slate-400' },
  no_change: { label: 'No change', cls: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200', dot: 'bg-slate-300' },
};
export const CLASSIFICATIONS = ['potential_change', 'needs_review', 'confirmed_change', 'likely_included', 'no_change'];

export const DECISION_META = {
  pursue: { label: 'Pursue', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-200' },
  monitor: { label: 'Monitor', cls: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200' },
  reject: { label: 'Reject', cls: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-200' },
  already_covered: { label: 'Already covered', cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' },
  duplicate: { label: 'Duplicate', cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' },
  insufficient_evidence: { label: 'Insufficient evidence', cls: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200' },
};
export const DECISIONS = ['pursue', 'monitor', 'reject', 'already_covered', 'duplicate', 'insufficient_evidence'];

export const PRICING_META = {
  unquantified: { label: 'Unquantified', cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', dot: 'bg-slate-400' },
  partially_priced: { label: 'Partially priced', cls: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200', dot: 'bg-amber-500' },
  fully_priced: { label: 'Fully priced', cls: 'bg-money-50 text-money-700 ring-1 ring-inset ring-money-200', dot: 'bg-money-600' },
  legacy: { label: 'Legacy (re-price)', cls: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200', dot: 'bg-violet-500' },
};

export const QUANTITY_SOURCES = ['Communication', 'Measurement sheet', 'Site record', 'Drawing', 'User entry'];
export const RATE_SOURCE_TYPES = [
  { value: 'boq', label: 'BOQ' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'manual_approved', label: 'Manual (approved)' },
];
export const RATE_BASIS = [
  { value: 'base', label: 'Base rate (before adjustment)' },
  { value: 'effective', label: 'Already-adjusted effective rate' },
];

// Format an authored amount in an explicit currency (NO FX conversion).
export const fmtAmount = (value, code = 'USD') => {
  const meta = CURRENCY_META[code] || CURRENCY_META.USD;
  try {
    return new Intl.NumberFormat(meta.locale, { style: 'currency', currency: meta.code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
  } catch { return `${meta.symbol}${Number(value || 0).toFixed(2)}`; }
};
